import { assign } from 'xstate';
import { accountSummaryV2CacheKey, queryFnFactory as accountSummaryQueryFunction } from '@syf-api-library/account-summary';
import { alternatePaymentMethodsCacheKey, deletionFnFactory as alternatePaymentMethodsDeletionFunction } from '@syf-api-library/alternate-payment-methods';
import { autopayStatusCacheKey, mutationFnFactory as autopayStatusMutationFunction, queryFnFactory as autopayStatusQueryFunction } from '@syf-api-library/autopay-status';
import { formatDateStringToObject } from 'syf-js-utilities/formats';
import generateUUID from 'syf-js-utilities/helpers/generateUUID';
import { VALIDATION_ERROR_CODE } from '../../const/errorCode';
import { paymentOptions } from '../../const/paymentOptions';
import { generateAutopayPayload, generatePaymentMutationPayload, getEstDateString, getPaymentAmountFromOption, getTodaysTimeOffset, isInstallmentLoan, isRevolvingCreditProduct } from '../../helpers';
import isPaymentMethodErrorCode from '../../helpers/isPaymentMethodErrorCode';
import { paymentsUrl } from '../../hooks/syf-api/api/endpoints';
import { getFormattedPayloadFromVariablesACH, getFormattedPayloadFromVariablesCard } from '../../hooks/syf-api/payments';
import { getValues } from '../../xform';
export const assignAvailableCredit = (key) => assign({
    [key]: ({ apiMachineRef }) => {
        const { data: accountSummary } = apiMachineRef.getSnapshot().context.accountSummaryQueryResult;
        return Number(accountSummary.credit.availableCredit);
    }
});
const getTrackingHeaders = () => {
    return {
        'x-syf-request-trackingid': generateUUID()
    };
};
const getAuthHeaders = () => {
    const accessToken = sessionStorage.getItem('accessToken');
    const correlationId = sessionStorage.getItem('correlationId');
    return {
        Authorization: `Bearer ${accessToken}`,
        'x-syf-request-correlationid': correlationId || ''
    };
};
const getPaymentAmount = (oneTimePaymentFormValues, apiMachineContext) => {
    const { paymentOption, paymentAmount } = oneTimePaymentFormValues;
    return paymentOption === paymentOptions.OTHER_PAYMENT
        ? paymentAmount
        : getPaymentAmountFromOption(paymentOption, apiMachineContext);
};
const createPaymentRequestBody = (accountId, accountIdType, mutationVariables) => {
    const { amount, date, paymentMedium } = mutationVariables;
    const commonRequestFields = {
        'cipher.accountId': accountId,
        accountIdType,
        amount,
        date
    };
    if (paymentMedium !== 'E_CHECK') {
        /**
         * DCH debit card payment methods will be persisted with alternate payment methods
         * so we must keep using the getFormattedPayloadFromVariablesCard function to parse that data.
         */
        return Object.assign(Object.assign({}, commonRequestFields), { paymentMedium, paymentCardInfo: getFormattedPayloadFromVariablesCard(mutationVariables) });
    }
    const { paymentMethod } = mutationVariables;
    return Object.assign(Object.assign({}, commonRequestFields), { paymentMedium,
        paymentMethod });
};
// eslint-disable-next-line max-lines-per-function
export const makePayment = async ({ apiMachineRef, featureFlags, oneTimePaymentFormMachineRef, payoffPaymentFormMachineRef }) => {
    let formValues;
    let paymentScheduledDate;
    let paymentAmount;
    const apiMachineContext = apiMachineRef.getSnapshot().context;
    const { alternatePaymentMethodsQueryResult, apiConfig: { keyProps }, paymentMethodsQueryResult } = apiMachineContext;
    const { data: paymentMethodData } = paymentMethodsQueryResult;
    const { data: alternatePaymentMethodData } = alternatePaymentMethodsQueryResult;
    const paymentMethods = [
        ...paymentMethodData.paymentMethods,
        ...alternatePaymentMethodData.paymentMethods
    ];
    if (oneTimePaymentFormMachineRef) {
        formValues = getValues(oneTimePaymentFormMachineRef.getSnapshot().context.actors);
        const { paymentDate } = formValues;
        paymentScheduledDate = new Date(formatDateStringToObject(paymentDate).getTime() + getTodaysTimeOffset());
        paymentAmount = getPaymentAmount(formValues, apiMachineContext);
    }
    if (payoffPaymentFormMachineRef) {
        formValues = getValues(payoffPaymentFormMachineRef.getSnapshot().context.actors);
        paymentScheduledDate = formatDateStringToObject(formValues.paymentDate);
        paymentAmount = formValues.paymentAmount;
    }
    const paymentMethod = paymentMethods === null || paymentMethods === void 0 ? void 0 : paymentMethods.find((method) => {
        return `${method === null || method === void 0 ? void 0 : method.id}` === formValues.paymentMethod;
    });
    const mutationVariables = generatePaymentMutationPayload(paymentScheduledDate, paymentAmount, paymentMethod);
    const { amount, date, paymentMedium } = mutationVariables;
    const { accountId, accountIdType } = keyProps;
    const requestBody = featureFlags.isPLAddPaymentMethodsEnabled
        ? createPaymentRequestBody(accountId, accountIdType, mutationVariables)
        : Object.assign({ accountIdType,
            amount, 'cipher.accountId': accountId, date,
            paymentMedium }, (paymentMedium === 'E_CHECK'
            ? {
                paymentMethod: getFormattedPayloadFromVariablesACH(mutationVariables)
            }
            : {
                paymentCardInfo: getFormattedPayloadFromVariablesCard(mutationVariables)
            }));
    const { apiConfig } = apiMachineContext;
    const fullUrl = new URL(paymentsUrl, apiConfig.config.hosts.apigee);
    const response = await fetch(fullUrl.toString(), {
        mode: 'cors',
        headers: Object.assign(Object.assign({ 'Content-Type': 'application/json', Accept: 'application/json' }, getAuthHeaders()), getTrackingHeaders()),
        method: 'POST',
        body: JSON.stringify(requestBody)
    });
    const status = response === null || response === void 0 ? void 0 : response.status;
    let body = null;
    try {
        body = await (response === null || response === void 0 ? void 0 : response.json());
    }
    catch (_a) {
        // noop
    }
    if (!response.ok) {
        const error = new Error();
        error.body = body;
        error.status = status;
        throw error;
    }
    return {
        body,
        status
    };
};
// eslint-disable-next-line max-lines-per-function
export const enrollInOneClickAutopay = async ({ apiMachineRef, oneTimePaymentFormMachineRef }) => {
    const oneTimePaymentForm = getValues(oneTimePaymentFormMachineRef.getSnapshot().context.actors);
    const apiMachineContext = apiMachineRef.getSnapshot().context;
    const { apiConfig: { config: mfeConfig, keyProps }, queryClient } = apiMachineContext;
    const { data: accountSummary } = apiMachineContext.accountSummaryQueryResult;
    const enroll = autopayStatusMutationFunction(mfeConfig, keyProps, {
        /*
         * Analytics uses API data to determine Successful One-Click Autopay enroll
         * This Header is required for API to differentiate between normal Autopay call and One-Click Autopay call
         */
        'X-SYF-PayMyBill-Autopay-OneClick': 'true'
    }, queryClient);
    const fetchAutopayStatus = autopayStatusQueryFunction(mfeConfig, keyProps);
    const { paymentOption: oneTimePaymentOption } = oneTimePaymentForm;
    const { paymentMethod } = oneTimePaymentForm;
    const productType = accountSummary.product.type;
    const paymentOption = oneTimePaymentOption === paymentOptions.TOTAL_AMOUNT_DUE &&
        isInstallmentLoan(productType)
        ? paymentOptions.MONTHLY_PAYMENT
        : oneTimePaymentOption;
    const paymentAmount = getPaymentAmount(oneTimePaymentForm, apiMachineContext);
    const autopayPayload = generateAutopayPayload({
        paymentOption,
        paymentAmount,
        paymentMethod,
        accountSummary
    });
    try {
        let autopayStatusPayload = await enroll(autopayPayload);
        /*
         * If the API doesn't return the autopay status as the response body
         * we have to manually re-hydrate the autopay status
         */
        if (autopayStatusPayload === null) {
            autopayStatusPayload = await fetchAutopayStatus();
        }
        // Set React Query cache data
        queryClient.setQueryData(autopayStatusCacheKey(keyProps), autopayStatusPayload);
    }
    catch (_a) {
        throw new Error('Autopay enrollment error');
    }
};
export const maybeDeleteAlternatePaymentMethod = async ({ apiMachineRef, oneTimePaymentFormMachineRef, payoffPaymentFormMachineRef, paymentStatus, paymentBody }) => {
    var _a;
    const formValues = getValues(oneTimePaymentFormMachineRef.getSnapshot().context.actors ||
        payoffPaymentFormMachineRef.getSnapshot().context.actors);
    const { paymentMethod: paymentMethodId } = formValues;
    const apiMachineContext = apiMachineRef.getSnapshot().context;
    const { alternatePaymentMethodsQueryResult, apiConfig, queryClient } = apiMachineContext;
    const { config: mfeConfig, keyProps } = apiConfig;
    const deleteAlternatePaymentMethod = alternatePaymentMethodsDeletionFunction(mfeConfig, keyProps);
    if (paymentStatus !== VALIDATION_ERROR_CODE) {
        return;
    }
    const shouldDeleteAlternatePaymentMethod = (_a = paymentBody === null || paymentBody === void 0 ? void 0 : paymentBody.businessErrors) === null || _a === void 0 ? void 0 : _a.some(({ code }) => isPaymentMethodErrorCode(code));
    /*
     * If there was an API error due to a bad payment method, we shouldn't offer that
     * as an option to the customer again. Let's remove it and force the customer to
     * re-enter the details
     */
    if (shouldDeleteAlternatePaymentMethod) {
        const paymentMethod = alternatePaymentMethodsQueryResult.data.paymentMethods.find(({ id }) => id === paymentMethodId);
        await deleteAlternatePaymentMethod(paymentMethod);
        queryClient.removeQueries(alternatePaymentMethodsCacheKey(keyProps));
    }
};
export const fetchAccountSummary = async ({ apiMachineRef }) => {
    /*
     * clearing of available credit for RTP clients takes atleast 100ms in middleware
     * and is done asynchronously. The middleware team wants us to wait 100ms after making
     * a payment before we try to re-fetch account summary
     */
    await new Promise((resolve) => {
        setTimeout(resolve, 100);
    });
    const { apiConfig, queryClient } = apiMachineRef.getSnapshot().context;
    const { config: mfeConfig, keyProps } = apiConfig;
    const fetchAccountSummaryQuery = accountSummaryQueryFunction(mfeConfig, keyProps);
    try {
        const accountSummary = await fetchAccountSummaryQuery();
        const cacheKey = accountSummaryV2CacheKey(keyProps);
        queryClient.setQueryData(cacheKey, accountSummary);
        return accountSummary;
    }
    catch (_a) {
        throw new Error('Account summary fetch error');
    }
};
const getShouldDisplayAvailableCredit = (productType, isPaymentForCurrentDate, isPaymentPending, isAvailableCreditPositive, hasAvailableCreditChanged) => {
    return (isRevolvingCreditProduct(productType) &&
        !isPaymentPending &&
        isPaymentForCurrentDate &&
        isAvailableCreditPositive &&
        hasAvailableCreditChanged);
};
// eslint-disable-next-line max-lines-per-function
export const sendResultToParentMachine = ({ apiMachineRef, availableCreditAfterPayment, availableCreditBeforePayment, hasOneClickAutopayError, paymentBody, paymentStatus, oneTimePaymentFormMachineRef, payoffPaymentFormMachineRef, shouldEnrollInOneClickAutopay }) => {
    var _a;
    const { accountSummaryQueryResult: { data: accountSummary } } = apiMachineRef.getSnapshot().context;
    let paymentForm;
    if (oneTimePaymentFormMachineRef) {
        paymentForm = getValues(oneTimePaymentFormMachineRef.getSnapshot().context.actors);
    }
    if (payoffPaymentFormMachineRef) {
        paymentForm = getValues(payoffPaymentFormMachineRef.getSnapshot().context.actors);
    }
    const isPaymentSuccessful = paymentStatus >= 200 && paymentStatus < 300;
    const { paymentDate } = paymentForm;
    const isPaymentForCurrentDate = paymentDate === getEstDateString(new Date());
    const hasPaymentMethodError = (_a = paymentBody === null || paymentBody === void 0 ? void 0 : paymentBody.businessErrors) === null || _a === void 0 ? void 0 : _a.some(({ code }) => isPaymentMethodErrorCode(code));
    const isPaymentPending = paymentStatus === 202;
    return {
        hasOneClickAutopayError: shouldEnrollInOneClickAutopay &&
            /*
             * We need to communicate back to the success/error page if one-click-autopay was
             * attempted, but failed (even if it was a one-time-payment error)
             */
            (!isPaymentSuccessful || hasOneClickAutopayError),
        hasPaymentMethodError,
        isPaymentPending,
        initialAvailableCredit: availableCreditBeforePayment,
        paymentId: paymentBody === null || paymentBody === void 0 ? void 0 : paymentBody.paymentId,
        shouldHaveEnrolledInOneClickAutopay: shouldEnrollInOneClickAutopay,
        shouldDisplayAvailableCredit: getShouldDisplayAvailableCredit(accountSummary.product.type, isPaymentForCurrentDate, isPaymentPending, availableCreditAfterPayment > 0, availableCreditAfterPayment > availableCreditBeforePayment)
    };
};
